'use client'

import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { type SourceKey, useWithdrawal } from '@/hooks/useWithdrawal'
import { DESTINATION_CHAINS, SOURCE_CHAINS, getExplorerUrl } from '@/lib/api/lifi'
import { useTrading } from '@/providers/TradingProvider'
import { useModalStore } from '@/stores/modal-store'
import {
	getAddressFormatHint,
	isValidAddressForChain,
	truncateAddress,
} from '@/utils/address-validation'
import {
	AlertCircle,
	ArrowDown,
	ArrowUpRight,
	CheckCircle2,
	ExternalLink,
	Globe,
	Loader2,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { StandardWithdrawView } from './StandardWithdrawView'

// ---------------------------------------------------------------------------
// Constants & helpers
// ---------------------------------------------------------------------------

const SOLANA_VALIDATION_CHAIN_ID = 792703809

function formatUsdc(raw: string, decimals = 6): string {
	const num = Number(raw) / 10 ** decimals
	return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export const SOURCE_OPTIONS: { key: SourceKey; label: string; sublabel: string }[] = [
	{ key: 'polygon', label: 'Polymarket', sublabel: 'Polygon' },
	{ key: 'solana', label: 'Kalshi', sublabel: 'Solana' },
	{ key: 'bsc', label: 'Myriad / Predict', sublabel: 'BNB Chain' },
]

type WithdrawMode = 'select' | 'crosschain' | 'standard'

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------

export function WithdrawModal() {
	const { activeModal, closeModal } = useModalStore()
	const isOpen = activeModal === 'withdraw'

	const [withdrawMode, setWithdrawMode] = useState<WithdrawMode>('select')

	// Get trading session for RelayClient (gas sponsorship on Polygon)
	const { relayClient, isReady } = useTrading()

	const {
		polygonBalance,
		solanaBalance,
		quote,
		fetchQuote,
		execute,
		status,
		error,
		txHashes,
		reset,
	} = useWithdrawal()

	// Form state (crosschain only)
	const [sourceKey, setSourceKey] = useState<SourceKey>('polygon')
	const [destinationChainId, setDestinationChainId] = useState<number>(DESTINATION_CHAINS[0].id)
	const [recipientAddress, setRecipientAddress] = useState('')
	const [amount, setAmount] = useState('')
	const [isMaxAmount, setIsMaxAmount] = useState(false)

	// Derived
	const destChain = useMemo(
		() => DESTINATION_CHAINS.find(c => c.id === destinationChainId),
		[destinationChainId]
	)
	const sourceBalance = sourceKey === 'polygon' ? polygonBalance : solanaBalance
	const addressValidationChainId = destChain?.chainType === 'SVM' ? SOLANA_VALIDATION_CHAIN_ID : 137
	const isValidAddress = recipientAddress
		? isValidAddressForChain(recipientAddress, addressValidationChainId)
		: true
	const amountNum = Number.parseFloat(amount) || 0
	const isValidAmount = amountNum > 0 && (isMaxAmount || amountNum <= sourceBalance)
	const canSubmit = isValidAddress && isValidAmount && recipientAddress.length > 0

	// Reset everything when modal closes
	useEffect(() => {
		if (!isOpen) {
			setWithdrawMode('select')
			setRecipientAddress('')
			setAmount('')
			setIsMaxAmount(false)
			setSourceKey('polygon')
			reset()
		}
	}, [isOpen, reset])

	const handleMax = useCallback(() => {
		setAmount(sourceBalance.toFixed(2))
		setIsMaxAmount(true)
	}, [sourceBalance])

	const handleGetQuote = useCallback(async () => {
		if (!canSubmit) return
		await fetchQuote({
			amount,
			sourceKey,
			destinationChainId,
			recipientAddress,
			useMaxBalance: isMaxAmount,
		})
	}, [amount, sourceKey, destinationChainId, recipientAddress, canSubmit, fetchQuote, isMaxAmount])

	const handleConfirm = useCallback(async () => {
		const needsPolygon = quote?.sourceChainId === SOURCE_CHAINS.polygon.id
		if (needsPolygon && !isReady) return
		execute(relayClient)
	}, [execute, relayClient, quote, isReady])

	const handleClose = useCallback(() => {
		if (status === 'executing' || status === 'polling' || status === 'approving') return
		closeModal()
	}, [status, closeModal])

	const handleBack = useCallback(() => {
		setWithdrawMode('select')
		setRecipientAddress('')
		setAmount('')
		setIsMaxAmount(false)
		setSourceKey('polygon')
		reset()
	}, [reset])

	return (
		<Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
			<DialogContent
				closeButtonContainerClasses="top-[21px] size-[26px]"
				className="gap-[24px] rounded-3xl border border-white/10 bg-brand-dark-light p-[24px] sm:max-w-[480px]"
			>
				<DialogHeader>
					<DialogTitle className="text-center text-[18px] text-white leading-[100%]">
						Withdraw USDC/USDT
					</DialogTitle>
					<DialogDescription className="text-center text-brand-gray-secondary leading-[100%]">
						Transfer USDC/USDT to an external wallet
					</DialogDescription>
				</DialogHeader>

				{/* ============================================================ */}
				{/* Mode Selection                                                */}
				{/* ============================================================ */}
				{withdrawMode === 'select' && (
					<div className="flex flex-col gap-[12px]">
						{/* Crosschain card */}
						<button
							type="button"
							onClick={() => setWithdrawMode('crosschain')}
							className="flex items-start gap-[16px] rounded-[16px] border border-brand-gray bg-brand-dark p-[20px] text-left transition-colors hover:border-white/20 hover:bg-white/5"
						>
							<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-500/15 text-blue-400">
								<Globe className="h-5 w-5" />
							</div>
							<div className="flex flex-col gap-[4px]">
								<p className="font-medium text-[16px] text-white leading-[100%]">
									Crosschain Withdrawal
								</p>
								<p className="text-[13px] text-white/50 leading-[140%]">
									Withdraw to any chain — Ethereum, Base, Arbitrum, Solana, and more. Powered by
									LI.FI.
								</p>
							</div>
						</button>

						{/* Standard card */}
						<button
							type="button"
							onClick={() => setWithdrawMode('standard')}
							className="flex items-start gap-[16px] rounded-[16px] border border-brand-gray bg-brand-dark p-[20px] text-left transition-colors hover:border-white/20 hover:bg-white/5"
						>
							<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400">
								<ArrowUpRight className="h-5 w-5" />
							</div>
							<div className="flex flex-col gap-[4px]">
								<p className="font-medium text-[16px] text-white leading-[100%]">
									Standard Withdrawal
								</p>
								<p className="text-[13px] text-white/50 leading-[140%]">
									Send USDC on the same chain with no bridging fees. Polygon or Solana direct
									transfer.
								</p>
							</div>
						</button>
					</div>
				)}

				{/* ============================================================ */}
				{/* Standard Withdrawal (same-chain)                              */}
				{/* ============================================================ */}
				{withdrawMode === 'standard' && (
					<StandardWithdrawView onBack={handleBack} onDone={handleClose} />
				)}

				{/* ============================================================ */}
				{/* Crosschain Withdrawal (LI.FI)                                 */}
				{/* ============================================================ */}

				{/* Form View */}
				{withdrawMode === 'crosschain' &&
					(status === 'idle' || status === 'loading_quote' || status === 'error') && (
						<div className="flex flex-col gap-[24px]">
							{/* Source Toggle */}
							<div className="flex flex-col gap-[8px]">
								<p className="text-[16px] text-white/70 leading-[100%]">Withdraw from</p>
								<div className="flex rounded-[12px] border border-brand-gray bg-brand-dark p-[4px]">
									{SOURCE_OPTIONS.map(opt => (
										<button
											key={opt.key}
											type="button"
											onClick={() => {
												setSourceKey(opt.key)
												setIsMaxAmount(false)
											}}
											className={`flex flex-1 flex-col items-center justify-center gap-[6px] rounded-[8px] px-[8px] py-[10px] text-center text-[14px] leading-[100%] transition-colors ${
												sourceKey === opt.key
													? 'bg-white/10 text-white'
													: 'text-white/50 hover:text-white/70'
											}`}
										>
											<img
												src={SOURCE_CHAINS[opt.key].logoURI}
												alt={opt.label}
												className="h-6 w-6 shrink-0 rounded-full"
											/>
											<div className="flex flex-col items-center gap-[2px]">
												<span className="font-medium text-[12px] leading-tight">{opt.label}</span>
												<span className="text-[10px] text-white/40 leading-tight">
													{opt.sublabel}
												</span>
											</div>
										</button>
									))}
								</div>
							</div>

							{/* Amount Input */}
							<div className="flex flex-col gap-[12px]">
								<div className="flex flex-col gap-[12px] rounded-[16px] border border-brand-gray bg-brand-dark p-[20px]">
									<div className="flex items-center justify-between">
										<p className="text-[16px] text-white/70 leading-[100%]">Available Balance</p>
										<p className="text-[16px] text-white leading-[100%]">
											{sourceBalance.toFixed(2)} USDC
										</p>
									</div>
									<div className="flex items-center justify-between gap-[12px]">
										<div className="flex items-center gap-[8px] overflow-hidden">
											<input
												type="text"
												inputMode="decimal"
												value={amount}
												onChange={e => {
													const value = e.target.value
													if (value === '' || /^\d*\.?\d*$/.test(value)) {
														setAmount(value)
														setIsMaxAmount(false)
													}
												}}
												placeholder="0"
												className="field-sizing-content min-w-0 flex-1 bg-transparent text-[48px] text-white leading-[100%] [appearance:textfield] placeholder:text-white/30 focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
											/>
											<span className="shrink-0 text-[24px] text-white/50 leading-[100%]">
												USDC
											</span>
										</div>
										<button
											type="button"
											onClick={handleMax}
											className="shrink-0 rounded-[12px] bg-white/5 px-[16px] py-[10px] text-[14px] text-white leading-[100%] transition-colors hover:bg-white/20"
										>
											MAX
										</button>
									</div>
									{!isMaxAmount && amountNum > sourceBalance && (
										<p className="text-red-500 text-xs">Insufficient balance</p>
									)}
								</div>
							</div>

							{/* Recipient Address */}
							<div className="flex flex-col gap-[8px]">
								<p className="text-[16px] text-white/70 leading-[100%]">Recipient Address</p>
								<input
									type="text"
									value={recipientAddress}
									onChange={e => setRecipientAddress(e.target.value)}
									placeholder={getAddressFormatHint(addressValidationChainId)}
									className={`w-full rounded-[8px] border bg-brand-dark px-[12px] py-[15px] text-[14px] text-white leading-[100%] placeholder:text-white/75 focus:outline-none focus:ring-1 ${
										recipientAddress && !isValidAddress
											? 'border-red-500 focus:ring-red-500'
											: 'border-brand-gray focus:ring-brand-blue'
									}`}
								/>
								{recipientAddress && !isValidAddress && (
									<p className="mt-[2px] text-[16px] text-white/70 leading-[100%]">
										Invalid address format
									</p>
								)}
							</div>

							{/* Destination Chain */}
							<div className="flex flex-col gap-[8px]">
								<p className="text-[16px] text-white/70 leading-[100%]">Destination Chain</p>
								<Select
									value={destinationChainId.toString()}
									onValueChange={val => setDestinationChainId(Number.parseInt(val))}
								>
									<SelectTrigger className="h-[48px]! w-full rounded-[8px]! border-brand-gray! bg-brand-dark-light! p-[12px] text-[14px] text-white leading-[100%]">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{DESTINATION_CHAINS.map(chain => (
											<SelectItem key={chain.id} value={chain.id.toString()}>
												<div className="flex items-center gap-2">
													<img
														src={chain.logoURI}
														alt={chain.name}
														className="h-5 w-5 rounded-full"
													/>
													{chain.name}
												</div>
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							{/* Error */}
							{error && (
								<div className="flex items-center gap-2 rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-[16px] text-red-400 leading-[100%]">
									<AlertCircle className="h-4 w-4 shrink-0" />
									{error}
								</div>
							)}

							{/* Actions */}
							<div className="flex gap-[12px]">
								<Button
									variant="outline"
									onClick={handleBack}
									className="h-12 flex-1 rounded-[12px] border-white/10 text-white hover:bg-white/5"
								>
									Back
								</Button>
								<Button
									onClick={handleGetQuote}
									disabled={!canSubmit || status === 'loading_quote'}
									className="h-12 flex-[2] rounded-[12px] bg-brand-blue font-medium text-white hover:bg-brand-blue/90 disabled:opacity-50"
								>
									{status === 'loading_quote' ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											Getting Quote...
										</>
									) : (
										'Continue'
									)}
								</Button>
							</div>
						</div>
					)}

				{/* Quote Confirmation */}
				{withdrawMode === 'crosschain' && status === 'quote_ready' && quote && (
					<div className="flex flex-col gap-[24px]">
						<div className="rounded-[16px] border border-brand-gray bg-brand-dark p-[20px]">
							<div className="mb-[12px] flex items-center justify-between">
								<span className="text-[16px] text-white/70 leading-[100%]">You send</span>
								<span className="font-bold text-[24px] text-white leading-[100%]">
									${quote.inputAmount}
								</span>
							</div>
							<div className="flex items-center justify-center py-[8px]">
								<ArrowDown className="h-5 w-5 text-white/70" />
							</div>
							<div className="flex items-center justify-between">
								<span className="text-[16px] text-white/70 leading-[100%]">They receive</span>
								<span className="font-bold text-[24px] text-white leading-[100%]">
									~{formatUsdc(quote.estimatedOutput)} USDC
								</span>
							</div>
						</div>

						<div className="flex flex-col gap-[8px] rounded-[16px] border border-brand-gray bg-brand-dark p-[20px]">
							<div className="flex justify-between">
								<span className="text-[16px] text-white/70 leading-[100%]">Route</span>
								<span className="flex items-center gap-1.5 text-[16px] text-white leading-[100%]">
									{quote.toolLogoURI && (
										<img
											src={quote.toolLogoURI}
											alt={quote.toolName}
											className="h-4 w-4 rounded-full"
										/>
									)}
									{quote.toolName}
								</span>
							</div>
							<div className="flex justify-between">
								<span className="text-[16px] text-white/70 leading-[100%]">Source</span>
								<span className="flex items-center gap-1.5 text-[16px] text-white leading-[100%]">
									<img
										src={SOURCE_CHAINS[sourceKey].logoURI}
										alt={SOURCE_OPTIONS.find(o => o.key === sourceKey)?.label}
										className="h-4 w-4 rounded-full"
									/>
									{SOURCE_OPTIONS.find(o => o.key === sourceKey)?.label}
								</span>
							</div>
							<div className="flex justify-between">
								<span className="text-[16px] text-white/70 leading-[100%]">Destination</span>
								<span className="flex items-center gap-1.5 text-[16px] text-white leading-[100%]">
									{destChain?.logoURI && (
										<img
											src={destChain.logoURI}
											alt={destChain.name}
											className="h-4 w-4 rounded-full"
										/>
									)}
									{destChain?.name}
								</span>
							</div>
							<div className="flex justify-between">
								<span className="text-[16px] text-white/70 leading-[100%]">Recipient</span>
								<span className="font-mono text-[16px] text-white leading-[100%]">
									{truncateAddress(recipientAddress, 6)}
								</span>
							</div>
							<div className="flex justify-between">
								<span className="text-[16px] text-white/70 leading-[100%]">Network Fee</span>
								<span className="text-[16px] text-emerald-400 leading-[100%]">
									{quote.sourceChainId === SOURCE_CHAINS.polygon.id
										? 'Sponsored'
										: `$${quote.totalFeeUsd}`}
								</span>
							</div>
							{quote.sourceChainId === SOURCE_CHAINS.polygon.id &&
								Number(quote.totalFeeUsd) > 0 && (
									<div className="flex justify-between">
										<span className="text-[16px] text-white/70 leading-[100%]">Bridge Fee</span>
										<span className="text-[16px] text-white leading-[100%]">
											${quote.totalFeeUsd}
										</span>
									</div>
								)}
							<div className="flex justify-between">
								<span className="text-[16px] text-white/70 leading-[100%]">Min. Received</span>
								<span className="text-[16px] text-white leading-[100%]">
									{formatUsdc(quote.estimatedOutputMin)} USDC
								</span>
							</div>
							<div className="flex justify-between">
								<span className="text-[16px] text-white/70 leading-[100%]">Est. Time</span>
								<span className="text-[16px] text-white leading-[100%]">
									~
									{quote.timeEstimate < 60
										? `${quote.timeEstimate}s`
										: `${Math.ceil(quote.timeEstimate / 60)}m`}
								</span>
							</div>
						</div>

						<div className="flex gap-[12px]">
							<Button
								variant="outline"
								onClick={reset}
								className="h-12 flex-1 rounded-[12px] border-white/10 text-white hover:bg-white/5"
							>
								Back
							</Button>
							<Button
								onClick={handleConfirm}
								className="h-12 flex-1 rounded-[12px] bg-brand-blue font-medium text-white hover:bg-brand-blue/90"
							>
								Confirm Withdrawal
							</Button>
						</div>
					</div>
				)}

				{/* Approving / Executing / Polling */}
				{withdrawMode === 'crosschain' &&
					(status === 'approving' || status === 'executing' || status === 'polling') && (
						<div className="flex flex-col items-center gap-[16px] py-[32px]">
							<Loader2 className="h-12 w-12 animate-spin text-brand-blue" />
							<div className="text-center">
								<p className="font-medium text-[18px] text-white leading-[100%]">
									{status === 'approving'
										? 'Approving Token'
										: status === 'executing'
											? 'Processing Withdrawal'
											: 'Waiting for Confirmation'}
								</p>
								<p className="mt-[8px] text-[16px] text-white/70 leading-[100%]">
									{status === 'approving'
										? 'Setting up token approval for LI.FI...'
										: status === 'executing'
											? 'Submitting withdrawal transaction...'
											: 'Your transfer is being processed across chains...'}
								</p>
							</div>
						</div>
					)}

				{/* Success */}
				{withdrawMode === 'crosschain' && status === 'success' && (
					<div className="flex flex-col items-center gap-[16px] py-[32px]">
						<CheckCircle2 className="h-12 w-12 text-emerald-500" />
						<div className="text-center">
							<p className="font-medium text-[18px] text-white leading-[100%]">
								Withdrawal Complete!
							</p>
							<p className="mt-[8px] text-[16px] text-white/70 leading-[100%]">
								{quote?.estimatedOutput
									? `~${formatUsdc(quote.estimatedOutput)} USDC`
									: `$${quote?.inputAmount}`}{' '}
								sent to {truncateAddress(recipientAddress, 6)}
							</p>
						</div>
						{txHashes.length > 0 && (
							<div className="flex flex-wrap justify-center gap-[8px]">
								{txHashes.map((tx, idx) => (
									<a
										key={tx.hash}
										href={getExplorerUrl(tx.chainId, tx.hash)}
										target="_blank"
										rel="noopener noreferrer"
										className="flex items-center gap-1 rounded-[8px] bg-white/5 px-[12px] py-[8px] text-[14px] text-blue-400 leading-[100%] hover:bg-white/10"
									>
										{idx === 0 ? 'Source Transaction' : 'Target Transaction'}{' '}
										<ExternalLink className="h-3 w-3" />
									</a>
								))}
							</div>
						)}
						<Button
							onClick={handleClose}
							className="mt-[8px] h-12 w-full rounded-[12px] bg-brand-blue font-medium text-white hover:bg-brand-blue/90"
						>
							Done
						</Button>
					</div>
				)}
			</DialogContent>
		</Dialog>
	)
}
